import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { assetUrl } from '@/lib/assetUrl';
import {
  activityItem,
  activityTimeLabel,
  globalMessage,
  normalizeCharStats,
  type EventLogRow,
  type PreloadedSideData,
} from './characterProfileSide';

type StatsTab = 'victories' | 'combat' | 'globals' | 'activity';

const VICTORY_EVENTS = [
  { key: 'ba_wins' as const, emoji: '⚔️', name: 'Battle Arena' },
  { key: 'ctf_wins' as const, emoji: '🚩', name: 'Capture The Flag' },
  { key: 'rleague_wins' as const, emoji: '🏅', name: 'R-League' },
  { key: 'survival_wins' as const, emoji: '🛡️', name: 'Survival' },
  { key: 'fortress_wins' as const, emoji: '🏰', name: 'Fortress War' },
  { key: 'lms_wins' as const, emoji: '👑', name: 'Last Man Standing' },
];

interface CharacterStatsPanelProps {
  charId: number;
  side: PreloadedSideData | null;
}

async function fetchGlobals(charId: number) {
  const { data } = await api.get<{ success: boolean; data: EventLogRow[] }>(
    `/api/v1/character/${charId}/event-log`,
    { params: { type: 'global', page: 1, limit: 10 } },
  );
  return data.success && Array.isArray(data.data) ? data.data : [];
}

export function CharacterStatsPanel({ charId, side }: CharacterStatsPanelProps) {
  const [tab, setTab] = useState<StatsTab>('victories');

  const globalsQuery = useQuery({
    queryKey: ['character', charId, 'globals'],
    queryFn: () => fetchGlobals(charId),
    enabled: tab === 'globals' && side !== null,
  });

  useEffect(() => {
    setTab('victories');
  }, [charId]);

  if (!side) {
    return (
      <div className="character-stats-section">
        <div className="stats-loading-mini">
          <p>Loading stats...</p>
        </div>
      </div>
    );
  }

  const stats = normalizeCharStats(side.stats?.data);
  const activity = side.logs?.data ?? [];
  const globals = globalsQuery.data ?? [];

  return (
    <div className="character-stats-section">
      <div className="stats-main-container">
        <div className="stats-main-tabs">
          <button
            type="button"
            className={`stats-main-tab${tab === 'victories' ? ' active' : ''}`}
            title="Event Victories"
            onClick={() => setTab('victories')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18M4 22h16M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22M18 2H6v7a6 6 0 0 0 12 0V2z"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            type="button"
            className={`stats-main-tab${tab === 'combat' ? ' active' : ''}`}
            title="Combat History"
            onClick={() => setTab('combat')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            type="button"
            className={`stats-main-tab${tab === 'globals' ? ' active' : ''}`}
            title="Globals Sent"
            onClick={() => setTab('globals')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" />
              <path
                d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
                stroke="currentColor"
                strokeWidth="2.5"
              />
              <path d="M2 12h20" stroke="currentColor" strokeWidth="2.5" />
            </svg>
          </button>
          <button
            type="button"
            className={`stats-main-tab${tab === 'activity' ? ' active' : ''}`}
            title="Recent Activity"
            onClick={() => setTab('activity')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
              <polyline
                points="22 12 18 12 15 21 9 3 6 12 2 12"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="stats-tab-content">
          <div className={`stats-panel${tab === 'victories' ? ' active' : ''}`} data-panel="victories">
            <div className="stats-events-grid">
              {VICTORY_EVENTS.map((event) => {
                const count = stats[event.key];
                return (
                  <div key={event.key} className="stat-event-box">
                    <div className="stat-event-emoji">{event.emoji}</div>
                    <div className="stat-event-name">{event.name}</div>
                    <div className="stat-event-count">{count}</div>
                    <div className="stat-event-unit">Win{count !== 1 ? 's' : ''}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={`stats-panel${tab === 'combat' ? ' active' : ''}`} data-panel="combat">
            <div className="stats-combat-grid">
              <div className="stat-combat-box highlight-red">
                <div className="stat-combat-icon">⚔️</div>
                <div className="stat-combat-label">Total PVP Kills</div>
                <div className="stat-combat-value">{stats.total_pvp_kills}</div>
              </div>
              <div className="stat-combat-box highlight-gold">
                <div className="stat-combat-icon">👹</div>
                <div className="stat-combat-label">Unique Bosses</div>
                <div className="stat-combat-value">{stats.total_unique_kills}</div>
              </div>
              <div className="stat-combat-box highlight-blue">
                <div className="stat-combat-icon">📢</div>
                <div className="stat-combat-label">Globals Sent</div>
                <div className="stat-combat-value">{stats.total_globals_sent}</div>
              </div>
            </div>
          </div>

          <div className={`stats-panel${tab === 'globals' ? ' active' : ''}`} data-panel="globals">
            {globalsQuery.isLoading ? (
              <div className="stats-loading-mini">Loading globals...</div>
            ) : globals.length === 0 ? (
              <p className="no-data-compact">No globals sent yet</p>
            ) : (
              <div className="globals-container">
                {globals.map((log, index) => (
                  <div key={index} className="global-pill" data-global-id={index}>
                    <img
                      src={assetUrl('/assets/images/icon/item/etc/mall_global_chatting.webp')}
                      alt="Global"
                      className="global-icon-img"
                    />
                    <span className="global-text">&quot;{globalMessage(log)}&quot;</span>
                    <span className="global-date time-ago">{activityTimeLabel(log)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={`stats-panel${tab === 'activity' ? ' active' : ''}`} data-panel="activity">
            <div className="activity-list">
              {activity.length === 0 ? (
                <p className="no-data-compact">No recent activity</p>
              ) : (
                activity.map((log, index) => {
                  const item = activityItem(log);
                  return (
                    <div key={index} className={`activity-item ${item.className}`}>
                      <span className="activity-icon">{item.icon}</span>
                      <span className="activity-text">{item.text}</span>
                      <span className="activity-time time-ago">{activityTimeLabel(log)}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
